import { useCallback, useEffect, useState } from "react";
import BuilderApp from "./App";
import EnvironmentPage from "./EnvironmentPage";
import {
  getActiveEnvironment,
  getActiveEnvironmentKey,
  getEnvironmentList,
  getTokenStorageKey,
  setActiveEnvironmentKey,
  shouldShowEnvironmentSwitcher
} from "./environmentConfig";
import LoginPage from "./LoginPage";
import ManageLevelsPage from "./ManageLevelsPage";
import ManagePage from "./ManagePage";
import {
  checkWebBuilderAccountEligibility,
  getCurrentWebBuilderUser,
  loginWebBuilder,
  registerWebBuilderAccount
} from "./webBuilderApi";

const ROUTE_BUILDER = "/";
const ROUTE_ENVIRONMENT = "/environment";
const ROUTE_LOGIN = "/login";
const ROUTE_MANAGE = "/manage";
const ROUTE_MANAGE_LEVELS = "/manager/levels";
const ROUTE_MANAGE_LEVELS_LEGACY = "/manage/levels";
const RAW_BASE_URL = import.meta.env.BASE_URL || "/";
const BASE_PATH = RAW_BASE_URL.endsWith("/") ? RAW_BASE_URL.slice(0, -1) : RAW_BASE_URL;

function normalizeRoute(rawRoute) {
  if (!rawRoute) return ROUTE_BUILDER;
  return rawRoute.startsWith("/") ? rawRoute : `/${rawRoute}`;
}

function getCurrentRouteFromPathname(pathname) {
  let trimmed = pathname || "/";
  if (BASE_PATH && BASE_PATH !== "/" && trimmed.startsWith(BASE_PATH)) {
    trimmed = trimmed.slice(BASE_PATH.length) || "/";
  }
  const normalized = normalizeRoute(trimmed);
  if (normalized === ROUTE_MANAGE_LEVELS_LEGACY) {
    return ROUTE_MANAGE_LEVELS;
  }
  return normalized;
}

function buildPathForRoute(route) {
  const normalized = normalizeRoute(route);
  if (!BASE_PATH || BASE_PATH === "/") {
    return normalized;
  }
  if (normalized === ROUTE_BUILDER) {
    return `${BASE_PATH}/`;
  }
  return `${BASE_PATH}${normalized}`;
}

function setRoute(route, replace = false) {
  const normalized = normalizeRoute(route);
  const targetPath = buildPathForRoute(normalized);
  if (replace) {
    window.history.replaceState(null, "", targetPath);
  } else {
    window.history.pushState(null, "", targetPath);
  }
}

export default function RootApp() {
  const [route, setRouteState] = useState(() =>
    getCurrentRouteFromPathname(window.location.pathname)
  );
  const [environments, setEnvironments] = useState(() => getEnvironmentList());
  const [activeEnvironmentKey, setActiveEnvironmentKeyState] = useState(() => getActiveEnvironmentKey());
  const [token, setToken] = useState(() =>
    localStorage.getItem(getTokenStorageKey(getActiveEnvironmentKey())) || ""
  );
  const [currentUser, setCurrentUser] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [registerBusy, setRegisterBusy] = useState(false);
  const [registerError, setRegisterError] = useState("");
  const [registerCheckBusy, setRegisterCheckBusy] = useState(false);
  const [registerCheckError, setRegisterCheckError] = useState("");

  const activeEnvironment =
    environments.find((environment) => environment.key === activeEnvironmentKey) ||
    getActiveEnvironment();
  const showEnvironmentUi = shouldShowEnvironmentSwitcher();

  const navigate = useCallback((nextRoute, replace = false) => {
    const normalized = normalizeRoute(nextRoute);
    setRoute(normalized, replace);
    setRouteState(normalized);
  }, []);

  const syncEnvironmentState = useCallback((preferredKey = null) => {
    const resolvedKey = preferredKey ? setActiveEnvironmentKey(preferredKey) : getActiveEnvironmentKey();
    setEnvironments(getEnvironmentList());
    setActiveEnvironmentKeyState(resolvedKey);
    return resolvedKey;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(getTokenStorageKey(activeEnvironmentKey));
    setToken("");
    setCurrentUser(null);
    navigate(ROUTE_LOGIN, true);
  }, [activeEnvironmentKey, navigate]);

  const handleEnvironmentSelected = useCallback(
    (nextEnvironmentKey) => {
      syncEnvironmentState(nextEnvironmentKey);
      setLoginError("");
      setRegisterError("");
      setRegisterCheckError("");
      setToken("");
      setCurrentUser(null);
      setSessionLoading(true);
      navigate(ROUTE_LOGIN, true);
    },
    [navigate, syncEnvironmentState]
  );

  const handleEnvironmentDefinitionsChanged = useCallback(() => {
    syncEnvironmentState();
  }, [syncEnvironmentState]);

  useEffect(() => {
    function onPopState() {
      setRouteState(getCurrentRouteFromPathname(window.location.pathname));
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrapSession() {
      setSessionLoading(true);
      const storedToken = localStorage.getItem(getTokenStorageKey(activeEnvironmentKey)) || "";
      if (!storedToken) {
        if (!cancelled) {
          setCurrentUser(null);
          setToken("");
          setSessionLoading(false);
        }
        return;
      }

      try {
        const user = await getCurrentWebBuilderUser(storedToken);
        if (cancelled) return;
        setCurrentUser(user);
        setToken(storedToken);
      } catch {
        if (cancelled) return;
        localStorage.removeItem(getTokenStorageKey(activeEnvironmentKey));
        setCurrentUser(null);
        setToken("");
      } finally {
        if (!cancelled) {
          setSessionLoading(false);
        }
      }
    }

    bootstrapSession();
    return () => {
      cancelled = true;
    };
  }, [activeEnvironment?.baseUrl, activeEnvironmentKey]);

  useEffect(() => {
    if (sessionLoading) return;

    if (!currentUser && route !== ROUTE_LOGIN && route !== ROUTE_ENVIRONMENT) {
      navigate(ROUTE_LOGIN, true);
      return;
    }

    if (currentUser && route === ROUTE_LOGIN) {
      navigate(ROUTE_BUILDER, true);
    }
  }, [currentUser, navigate, route, sessionLoading]);

  async function handleLogin(username, password) {
    setLoginBusy(true);
    setLoginError("");
    try {
      const payload = await loginWebBuilder(username, password);
      localStorage.setItem(getTokenStorageKey(activeEnvironmentKey), payload.access_token);
      setToken(payload.access_token);
      setCurrentUser(payload.user);
      navigate(ROUTE_BUILDER, true);
    } catch (error) {
      setLoginError(error.message);
    } finally {
      setLoginBusy(false);
    }
  }

  async function handleRegister(friendlyId, password) {
    setRegisterBusy(true);
    setRegisterError("");
    try {
      const payload = await registerWebBuilderAccount(friendlyId, password);
      localStorage.setItem(getTokenStorageKey(activeEnvironmentKey), payload.access_token);
      setToken(payload.access_token);
      setCurrentUser(payload.user);
      navigate(ROUTE_BUILDER, true);
    } catch (error) {
      setRegisterError(error.message);
    } finally {
      setRegisterBusy(false);
    }
  }

  async function handleRegisterCheck(friendlyId) {
    setRegisterCheckBusy(true);
    setRegisterCheckError("");
    try {
      return await checkWebBuilderAccountEligibility(friendlyId);
    } catch (error) {
      setRegisterCheckError(error.message);
      throw error;
    } finally {
      setRegisterCheckBusy(false);
    }
  }

  if (sessionLoading) {
    return <main className="route-loading">Checking session...</main>;
  }

  if (route === ROUTE_ENVIRONMENT) {
    return (
      <div className="route-shell">
        <EnvironmentPage
          activeEnvironment={activeEnvironment}
          showEnvironmentUi={showEnvironmentUi}
          environments={environments}
          onEnvironmentSelected={handleEnvironmentSelected}
          onEnvironmentDefinitionsChanged={handleEnvironmentDefinitionsChanged}
          onNavigateBack={() => navigate(currentUser ? ROUTE_BUILDER : ROUTE_LOGIN, true)}
          backLabel={currentUser ? "Back to Builder" : "Back to Login"}
        />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <LoginPage
        activeEnvironment={activeEnvironment}
        showEnvironmentUi={showEnvironmentUi}
        onLogin={handleLogin}
        onRegisterCheck={handleRegisterCheck}
        onRegister={handleRegister}
        busy={loginBusy}
        error={loginError}
        registerCheckBusy={registerCheckBusy}
        registerCheckError={registerCheckError}
        registerBusy={registerBusy}
        registerError={registerError}
      />
    );
  }

  const isManager = currentUser.role === "admin" || currentUser.role === "mod";
  const isAdmin = currentUser.role === "admin";

  return (
    <div className="route-shell">
      {route === ROUTE_MANAGE ? (
        isManager ? (
          <ManagePage
            token={token}
            currentUser={currentUser}
            onLogout={logout}
            currentRoute={route}
            onNavigate={navigate}
          />
        ) : (
          <main className="manage-shell">
            <section className="manage-card">
              <h2>Access denied</h2>
              <p>Only admins and mods can access /manage.</p>
            </section>
          </main>
        )
      ) : route === ROUTE_MANAGE_LEVELS ? (
        isAdmin ? (
          <ManageLevelsPage
            token={token}
            currentUser={currentUser}
            onLogout={logout}
            currentRoute={route}
            onNavigate={navigate}
          />
        ) : (
          <main className="manage-shell">
            <section className="manage-card">
              <h2>Access denied</h2>
              <p>Only admins can access /manager/levels.</p>
            </section>
          </main>
        )
      ) : (
        <BuilderApp
          token={token}
          currentUser={currentUser}
          onLogout={logout}
          showEnvironmentUi={showEnvironmentUi}
          activeEnvironment={activeEnvironment}
        />
      )}
    </div>
  );
}
